import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { InventarioInicialApi } from "../api/InventarioInicialApi";
import { useCorrelativoInicialPallet } from "../../../../../../../ProduccionV2/hooks/useCorrelativoInicialPallet";
import { getErrorFromResponse } from "../../../../../../../../../../utility/utils";

export const TIPO_PACAS = "pacas";
export const TIPO_SOBRANTE = "sobrante";

export const useInventarioInicialForm = () => {
    const {
        getConfiguracion,
        getProductos,
        guardarInventarioInicial,
        eliminarInventarioInicial,
        validarEliminacionDetalle,
        eliminarDetalleInventario,

        cargando,
    } = InventarioInicialApi();

    // los pallets del inventario inicial dependen de este correlativo y a la
    // vez lo dejan de solo lectura, así que se recarga junto con el documento
    const correlativo = useCorrelativoInicialPallet();

    const [configuracion, setConfiguracion] = useState(null);
    const [detallesPacas, setDetallesPacas] = useState([]);
    const [detallesSobrante, setDetallesSobrante] = useState([]);

    const {
        control: formControl,
        register: formRegister,
        setValue: formSetValue,
        getValues: formGetValues,
        trigger: validateForm,
        formState: { errors: formErrors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            fecha_inicial: null,
            observaciones: "",
        },
    });

    const CargarConfiguracion = async () => {
        try {
            const response = await getConfiguracion();

            setConfiguracion(response);

            const inventario = response ? response.inventario_inicial : null;

            if (!inventario) {
                setDetallesPacas([]);
                setDetallesSobrante([]);
                return;
            }

            formSetValue("fecha_inicial", moment(inventario.fecha_inicial));
            formSetValue("observaciones", inventario.observaciones || "");

            setDetallesPacas(
                inventario.detalles_pacas.map((detalle) => ({
                    id: detalle.id,
                    producto_id: detalle.producto_id,
                    producto_nombre: detalle.producto_nombre,
                    es_conforme_bodega: detalle.es_conforme_bodega,
                    pacas: detalle.pacas,
                    identificador_fisico: detalle.identificador_fisico,
                    numero_pallet: detalle.numero_pallet,
                })),
            );

            setDetallesSobrante(
                inventario.detalles_sobrante.map((detalle) => ({
                    id: detalle.id,
                    producto_id: detalle.producto_id,
                    producto_nombre: detalle.producto_nombre,
                    es_conforme_bodega: detalle.es_conforme_bodega,
                    kg_secos: detalle.kg_secos,
                })),
            );
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    useEffect(() => {
        CargarConfiguracion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const agregarDetalle = (tipo, detalle) => {
        if (tipo === TIPO_PACAS) {
            setDetallesPacas((prev) => [...prev, detalle]);
            return;
        }

        setDetallesSobrante((prev) => [...prev, detalle]);
    };

    // quita la fila solo del formulario, sin recargar el documento, para no
    // perder las filas nuevas que aún no se guardan
    const quitarDetalle = (tipo, conservar) => {
        if (tipo === TIPO_PACAS) {
            setDetallesPacas((prev) => prev.filter(conservar));
            return;
        }

        setDetallesSobrante((prev) => prev.filter(conservar));
    };

    const EliminarDetalleGuardado = async (tipo, detalle) => {
        try {
            await eliminarDetalleInventario(detalle.id);

            NotificationManager.success(
                "Registro eliminado del inventario inicial",
                "Éxito",
                3000,
            );

            quitarDetalle(tipo, (item) => item.id !== detalle.id);
            correlativo.CargarEstado();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al eliminar el registro del inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const mensajeEliminacionDetalle = (tipo, detalle) => {
        if (tipo === TIPO_PACAS) {
            return (
                `Se eliminará el pallet ${detalle.numero_pallet} del ` +
                "inventario inicial. ¿Deseas continuar?"
            );
        }

        const conformidad = detalle.es_conforme_bodega
            ? "conforme"
            : "no conforme";

        return (
            `Se eliminará el sobrante ${conformidad} de ` +
            `${detalle.producto_nombre} del inventario inicial. ` +
            "¿Deseas continuar?"
        );
    };

    // Las filas nuevas solo existen en el formulario y se quitan sin
    // confirmación. Las guardadas se eliminan en el momento, y la confirmación
    // se pide solo si el servidor indica que todavía se pueden eliminar
    const EliminarDetalle = async (tipo, index) => {
        const detalles = tipo === TIPO_PACAS ? detallesPacas : detallesSobrante;
        const detalle = detalles[index];

        if (!detalle.id) {
            quitarDetalle(tipo, (_, i) => i !== index);
            return;
        }

        try {
            await validarEliminacionDetalle(detalle.id);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "No se puede eliminar el registro del inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
            return;
        }

        DecisionSwal(
            mensajeEliminacionDetalle(tipo, detalle),
            "Sí, eliminar",
            "Cancelar",
            () => EliminarDetalleGuardado(tipo, detalle),
        );
    };

    const GuardarInventario = async () => {
        const valores = formGetValues();

        // las filas guardadas no se reenvían: solo se agregan las nuevas
        const pacasNuevas = detallesPacas.filter((detalle) => !detalle.id);
        const sobranteNuevo = detallesSobrante.filter(
            (detalle) => !detalle.id,
        );

        const body = {
            fecha_inicial: moment(valores.fecha_inicial).toISOString(),
            observaciones: valores.observaciones,
            detalles_pacas: pacasNuevas.map((detalle) => ({
                producto_id: detalle.producto_id,
                es_conforme_bodega: detalle.es_conforme_bodega,
                pacas: detalle.pacas,
                identificador_fisico: detalle.identificador_fisico,
            })),
            detalles_sobrante: sobranteNuevo.map((detalle) => ({
                producto_id: detalle.producto_id,
                es_conforme_bodega: detalle.es_conforme_bodega,
                kg_secos: detalle.kg_secos,
            })),
        };

        try {
            await guardarInventarioInicial(inventarioId(), body);

            NotificationManager.success(
                "Inventario inicial guardado con éxito",
                "Éxito",
                3000,
            );

            CargarConfiguracion();
            correlativo.CargarEstado();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al guardar el inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    // se valida antes de pedir la confirmación, y el foco pasa al primer campo
    // con error para que la página haga scroll hasta él
    const ValidarFormulario = () =>
        validateForm(undefined, { shouldFocus: true });

    const EliminarInventario = async () => {
        try {
            await eliminarInventarioInicial(inventarioId());

            NotificationManager.success(
                "Inventario inicial eliminado",
                "Éxito",
                3000,
            );

            formSetValue("fecha_inicial", null);
            formSetValue("observaciones", "");

            CargarConfiguracion();
            correlativo.CargarEstado();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al eliminar el inventario inicial",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const inventario = configuracion ? configuracion.inventario_inicial : null;

    const inventarioId = () => (inventario ? inventario.id : null);

    // sin configuración cargada todavía se asume editable, para no bloquear el
    // formulario mientras responde el servidor
    const editable = configuracion ? configuracion.editable : true;

    const fechaMaxima = (
        configuracion && configuracion.fecha_maxima
            ? moment(configuracion.fecha_maxima)
            : null
    );

    const primerMovimiento = (
        configuracion && configuracion.primer_movimiento
            ? moment(configuracion.primer_movimiento)
            : null
    );

    return {
        formControl,
        formRegister,
        formErrors,

        correlativo,

        inventario,
        editable,
        fechaMaxima,
        primerMovimiento,

        detallesPacas,
        detallesSobrante,
        agregarDetalle,
        EliminarDetalle,

        getProductos,
        ValidarFormulario,
        GuardarInventario,
        EliminarInventario,
        cargando,
    };
};
